import React from 'react';
import { Icon, Typography, Spinner } from '@scale/scaleui';
import { useSignupFlowToJobPostsForOnboardingFlowQuery } from '../../api';
import { useListJobConfigsQuery } from 'react-scale/corp/lib/api/growth';
import PanelEntry from '../PanelEntry';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { ScreenFilter } from '@scale/shared-types';

interface ResumeConfigEntryProps {
  resumeScreenConfigId?: string;
  resumeScreenConfigName?: string;
}

interface DetailPanelResumeScreenProps {
  ocpFlowId?: string;
  nodeResumeScreenConfigId?: string;
  nodeResumeScreenConfigNodeOnly?: boolean;
}

const ResumeConfigEntry: React.FC<ResumeConfigEntryProps> = ({
  resumeScreenConfigId,
  resumeScreenConfigName,
}) => (
  <>
    {resumeScreenConfigId ? (
      <>
        <Typography.Paragraph3 className="mx-1">signups are screened with</Typography.Paragraph3>
        <a
          href={`/corp${Routes.ResumeScreening}/${resumeScreenConfigId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center"
        >
          <Typography.Paragraph3>{resumeScreenConfigName}</Typography.Paragraph3>
          <Icon icon={['fas', 'external-link-alt']} variant="primary" />
        </a>
      </>
    ) : (
      <Typography.Paragraph3 className="ml-2">signups automatically pass</Typography.Paragraph3>
    )}
  </>
);

const DetailPanelResumeScreen: React.FC<DetailPanelResumeScreenProps> = ({
  ocpFlowId,
  nodeResumeScreenConfigId,
  nodeResumeScreenConfigNodeOnly,
}) => {
  const resumeScreenConfigsQuery = useListJobConfigsQuery(ScreenFilter.Growth);
  const resumeScreenConfigLookup = resumeScreenConfigsQuery.data?.reduce(
    (acc: Record<string, string>, config) => {
      acc[config._id] = config.name;
      return acc;
    },
    {},
  );
  const jobPostsQuery = useSignupFlowToJobPostsForOnboardingFlowQuery(ocpFlowId);
  const jobPosts = Object.values(jobPostsQuery.data || {}).flat();
  const signupFlowsMissingJobPosts = Object.entries(jobPostsQuery.data || {})
    .filter(([_signupFlowName, jobPostsArray]) => !jobPostsArray.length)
    .map(([signupFlowName]) => signupFlowName);

  const isLoading = jobPostsQuery.isLoading || resumeScreenConfigsQuery.isLoading;

  return (
    <PanelEntry title="Configuration">
      {nodeResumeScreenConfigNodeOnly ? (
        <div>
          <Typography.Caption>
            <b>All</b> users will be screened by this node's screening. If it is not set, users will
            automatically pass.
          </Typography.Caption>
          {isLoading ? (
            <Spinner size="sm" variant="primary" />
          ) : (
            <div className="flex items-center">
              <Typography.Paragraph3>All</Typography.Paragraph3>
              <ResumeConfigEntry
                resumeScreenConfigId={nodeResumeScreenConfigId}
                resumeScreenConfigName={resumeScreenConfigLookup?.[nodeResumeScreenConfigId || '']}
              />
            </div>
          )}
        </div>
      ) : (
        <div>
          <Typography.Caption>
            If users signed up via a job post with a resume screen, they will be screened
            accordingly. Otherwise, they will be screened by this node's screening. If neither is
            set, users will automatically pass.
          </Typography.Caption>
          {isLoading ? (
            <Spinner size="sm" variant="primary" className="mt-3" />
          ) : (
            <>
              {jobPosts.map(jobPost => {
                const resumeScreenConfigId =
                  jobPost.resumeScreeningConfigId || nodeResumeScreenConfigId;
                const resumeScreenConfigName =
                  resumeScreenConfigLookup?.[resumeScreenConfigId || ''];

                return (
                  <div key={jobPost.id} className="flex items-center mt-1">
                    <a
                      href={`/corp${Routes.JobPostSetup}/${jobPost.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center"
                    >
                      <Typography.Paragraph3>{jobPost.name}</Typography.Paragraph3>
                      <Icon icon={['fas', 'external-link-alt']} variant="primary" />
                    </a>
                    <ResumeConfigEntry
                      resumeScreenConfigId={resumeScreenConfigId}
                      resumeScreenConfigName={resumeScreenConfigName}
                    />
                  </div>
                );
              })}
              {!!signupFlowsMissingJobPosts.length && (
                <div className="flex items-center">
                  <Typography.Paragraph3>
                    Signup flows
                    <br />
                    {signupFlowsMissingJobPosts.map((flow, index) => (
                      <>
                        <span className="font-medium">{flow}</span>
                        {index < signupFlowsMissingJobPosts.length - 1 && <br />}
                      </>
                    ))}
                  </Typography.Paragraph3>
                  <ResumeConfigEntry
                    resumeScreenConfigId={nodeResumeScreenConfigId}
                    resumeScreenConfigName={
                      resumeScreenConfigLookup?.[nodeResumeScreenConfigId || '']
                    }
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </PanelEntry>
  );
};

export default DetailPanelResumeScreen;
